import {
  Chip,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { hostname, parseError } from "../../utils";

export default ({
  actionParameters,
  setActionParameters,
  currentPlan,
  headers,
  existingRecord,
  setValidForm,
  getToken,
  companyId,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  currentPlan: string;
  headers: Array<any>;
  existingRecord: boolean;
  setValidForm: (b: boolean) => void;
  getToken: Function;
  companyId: string;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [imageModels, setImageModels] = useState<Array<any>>([]);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    setValidForm(actionParameters.prompt && actionParameters.model);
  }, [actionParameters]);

  useEffect(() => {
    getImageModels();
  }, []);

  useEffect(() => {
    // Set model-specific dimensions when model changes
    if (actionParameters.model && imageModels.length > 0) {
      const selectedModel = imageModels.find((x) => x.id === actionParameters.model);
      if (selectedModel && selectedModel.provider === "Leonardo AI") {
        setActionParameters((prev: any) => ({
          ...prev,
          width: selectedModel.default_width || selectedModel.modelWidth,
          height: selectedModel.default_height || selectedModel.modelHeight,
        }));
      }
    }
  }, [actionParameters.model, imageModels]);

  const getImageModels = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/api/v0/images/models`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setImageModels(response.data);
      // If no current model is selected and we have models, set the first one
      if (!actionParameters.model && response.data.length > 0) {
        setActionParameters({ ...actionParameters, model: response.data[0].id });
      }
    } catch (err) {
      console.error("Error loading image models:", err);
    }
  };

  const handleInputChange = (event: any) => {
    const newValue = event.target.value;
    setActionParameters({ ...actionParameters, prompt: newValue });
    // Check for "/" and the cursor position

    if (
      inputRef.current &&
      newValue[inputRef.current.selectionStart - 1] === "/"
    ) {
      setAnchorEl(inputRef.current);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const selectedModel = imageModels.find((x) => x.id === actionParameters.model);

  return (
    <div>
      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Image Model
          <Tooltip title="GPT Image: Best for realistic and creative images. Leonardo AI: Great for artistic styles and variations. Google: Good for general purpose images. Different models have different quality levels and costs.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Choose your AI image model. Different models have different strengths
          and costs.
        </Typography>
        <FormControl fullWidth style={{ marginTop: 8 }}>
          <InputLabel>Model</InputLabel>
          <Select
            label="Model"
            value={actionParameters.model || "gpt-image-1"}
            onChange={(e) =>
              setActionParameters({ ...actionParameters, model: e.target.value })
            }
          >
            {imageModels.map((imageModel, index) => (
              <MenuItem value={imageModel.id} key={index}>
                <Chip
                  label={imageModel.provider}
                  style={{ marginRight: 5 }}
                />
                {imageModel.tags &&
                  imageModel.tags.map((tag: any) => (
                    <Chip key={tag} label={tag} style={{ marginRight: 5 }} />
                  ))}
                {imageModel.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {["gpt-image-1", "gpt-image-1.5", "gpt-image-2"].includes(actionParameters.model) && (
        <React.Fragment>
          <div style={{ marginTop: "16px" }}>
            <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              Image Settings
              <Tooltip title="Quality affects detail and generation time. Size determines output dimensions - square (1024x1024) is standard, wide/tall options for banners or portraits.">
                <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
              </Tooltip>
            </Typography>
            <Typography variant="body2">
              Configure image generation parameters for GPT Image.
            </Typography>
            
            <FormControl fullWidth style={{ marginTop: 8 }}>
              <InputLabel>Quality</InputLabel>
              <Select
                label="Quality"
                value={actionParameters.quality || "auto"}
                onChange={(e) =>
                  setActionParameters({ ...actionParameters, quality: e.target.value })
                }
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="auto">Auto</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth style={{ marginTop: 16 }}>
              <InputLabel>Size</InputLabel>
              <Select
                label="Size"
                value={actionParameters.size || "1024x1024"}
                onChange={(e) =>
                  setActionParameters({ ...actionParameters, size: e.target.value })
                }
              >
                <MenuItem value="1024x1024">1024x1024</MenuItem>
                <MenuItem value="1536x1024">1536x1024 (wide)</MenuItem>
                <MenuItem value="1024x1536">1024x1536 (tall)</MenuItem>
              </Select>
            </FormControl>
          </div>
        </React.Fragment>
      )}

      <div style={{ marginTop: "24px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Image Prompt
          <Tooltip title="Describe the image in detail: subject, style, colors, mood, composition. Use {{Column Name}} to personalize images with row data. Be specific about what you want to see.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Describe the image you want the AI to generate. Use column references
          to include data from the current row for personalized images.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={6}
          placeholder="Create a professional headshot of {{First Name}} {{Last Name}} for {{Company Name}}. The person should be wearing business attire and smiling professionally. Background should be clean and corporate."
          variant="outlined"
          value={actionParameters.prompt || ""}
          onChange={handleInputChange}
          inputRef={inputRef}
          style={{ marginTop: "8px" }}
        />
        <Menu
          anchorEl={anchorEl}
          open={showDropdown}
          onClose={() => setShowDropdown(false)}
          sx={{ maxHeight: 300 }}
        >
          <MenuItem
            onClick={() => {
              setActionParameters({
                ...actionParameters,
                prompt:
                  (actionParameters.prompt || "").substring(
                    0,
                    (actionParameters.prompt || "").length - 1
                  ) + `{{Company Description}}`,
              });
              setShowDropdown(false);
            }}
          >
            Company Description
          </MenuItem>
          <MenuItem
            onClick={() => {
              setActionParameters({
                ...actionParameters,
                prompt:
                  (actionParameters.prompt || "").substring(
                    0,
                    (actionParameters.prompt || "").length - 1
                  ) + `{{Full Record}}`,
              });
              setShowDropdown(false);
            }}
          >
            Full Record
          </MenuItem>
          {headers.map((header, i) => (
            <MenuItem
              key={i}
              onClick={() => {
                setActionParameters({
                  ...actionParameters,
                  prompt:
                    (actionParameters.prompt || "").substring(
                      0,
                      (actionParameters.prompt || "").length - 1
                    ) + `{{${header.field}}}`,
                });
                setShowDropdown(false);
              }}
            >
              {header.field}
            </MenuItem>
          ))}
        </Menu>
        <Typography variant="caption" style={{ marginTop: "4px" }}>
          Type{" "}
          <span
            style={{
              backgroundColor: "lightgray",
              padding: "2px 4px",
              borderRadius: "4px",
              color: "black",
            }}
          >
            /
          </span>{" "}
          to insert row information or your Company Description
        </Typography>
      </div>
    </div>
  );
};